import React, { useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import { tokens } from "./tokens";
import { Card, Eyebrow } from "./styled";
import Chip from "./Chip";
import WindowToggle from "./WindowToggle";
import NameToggle from "./NameToggle";
import SegmentedControl from "./SegmentedControl";
import FilterChips, { titleCase } from "./FilterChips";
import ConfigPanel from "./ConfigPanel";
import ConfigActionBar from "./ConfigActionBar";
import { ChangeHistoryTrigger } from "./ChangeHistory";
import StateBadge from "./StateBadge";
import ReadinessBadge from "./ReadinessBadge";
import CapabilityCell from "./CapabilityCell";
import { capabilityLadder } from "./capability";
import TractorMiniMap from "./TractorMiniMap";
import ReadinessWeekCard from "./ReadinessWeekCard";
import { ConfigSchema, VehicleRow } from "./configTypes";
import {
  attentionItems,
  daysSince,
  describeAge,
  freshnessEnabled,
} from "./configModel";
import { useConfigEditor } from "./useConfigEditor";
import { matchesQuery } from "./fleetSearch";
import {
  Filter,
  isTestRig,
  jiraUrl,
  NameMode,
  OP_CONFIG,
  primaryName,
  secondaryName,
  severityRank,
  SortBy,
  SortDir,
  Ticket,
  TicketSortField,
  Vehicle,
  vehicleComparator,
} from "./data";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { buttonVariants } from "../../components/ui/button";
import { TooltipProvider } from "../../components/ui/tooltip";
import { cn } from "../../lib/utils";

interface TractorDetailViewProps {
  vehicle: Vehicle;
  /** Whole fleet — powers the sibling navigation rail. */
  vehicles: Vehicle[];
  schema: ConfigSchema | null;
  /** Cognito ID token; absent in snapshot mode. */
  token?: string;
  /** Set when editing is unavailable, explaining why. */
  readOnlyReason?: string | null;
  /** True while the single-vehicle fetch (tickets + history) is in flight. */
  detailLoading?: boolean;
  /** Receives the server's row after a successful edit. */
  onSaved: (row: VehicleRow) => void;
  /** Active fleet filter, so the rail lists the same set the grid did. */
  filter: Filter;
  /** Change the filter from the detail too — kept in sync with the grid. */
  onFilter: (f: Filter) => void;
  /** Whether test rigs are listed in the rail — the same choice as the grid's. */
  showRigs: boolean;
  nameMode: NameMode;
  onNameMode: (m: NameMode) => void;
  onOpen: (vehicleName: string) => void;
  onBack: () => void;
}

// Rail sort options — name first (the default), then by open-ticket count.
const RAIL_SORTS: readonly { value: SortBy; label: string }[] = [
  { value: "name", label: "Name" },
  { value: "tickets", label: "Tickets" },
];

/** The cards that can take the whole main column. */
type Pane = "config" | "tickets" | "map";

/** Where the columns start scrolling inside themselves instead of the page. */
const FILL_MIN_WIDTH = 901;

const shortDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");

/**
 * How long a ticket has been open, from when it was raised.
 *
 * The date alone says when something last moved; the age says how long the
 * problem has been standing, which is what decides whether a MAJOR from March
 * is a MAJOR anyone is still working. Coarsens with distance the way people
 * say it: days, then months, then years.
 */
const openFor = (created: string | null, now: number): string | null => {
  const days = daysSince(created, now);
  if (days === null) return null;
  if (days <= 0) return "opened today";
  if (days === 1) return "open 1 day";
  if (days < 60) return `open ${days} days`;
  if (days < 730) return `open ${Math.round(days / 30)} months`;
  return `open ${Math.round(days / 365)} years`;
};

const ticketTime = (iso: string | null) =>
  iso ? new Date(iso).getTime() : NaN;

// Ascending by date; undated tickets always sort to the bottom either way.
const cmpDate = (a: string | null, b: string | null) => {
  const x = ticketTime(a);
  const y = ticketTime(b);
  if (Number.isNaN(x) && Number.isNaN(y)) return 0;
  if (Number.isNaN(x)) return 1;
  if (Number.isNaN(y)) return -1;
  return x - y;
};

const TractorDetailView: React.FC<TractorDetailViewProps> = ({
  vehicle: v,
  vehicles,
  schema,
  token,
  readOnlyReason,
  detailLoading,
  onSaved,
  filter,
  onFilter,
  showRigs,
  nameMode,
  onNameMode,
  onOpen,
  onBack,
}) => {
  const [sortField, setSortField] = useState<TicketSortField>("severity");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  // The rail sorts independently of the fleet grid — default to tractor name.
  const [railSort, setRailSort] = useState<SortBy>("name");
  const [railQuery, setRailQuery] = useState("");
  // The navigation a leave-confirmation is currently asking about, if any.
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  // One card at a time may take the whole main column, the others hidden.
  // Survives hopping tractors in the rail on purpose: someone comparing two
  // records wants the record big on both.
  const [maximized, setMaximized] = useState<Pane | null>(null);
  // The customer filter lives inside the rail behind a toggle. It opens by
  // itself when a filter is already narrowing the list — otherwise the count
  // would read "10 of 15" with nothing on screen to explain it.
  const [filterOpen, setFilterOpen] = useState(filter !== "all");
  // The customer the list is narrowed to, or null for the whole fleet. Drives
  // the lit state of the toggle and the name it announces.
  const appliedFilter = filter !== "all" ? titleCase(filter) : null;

  const toggleWindow = (pane: Pane) =>
    setMaximized((current) => (current === pane ? null : pane));

  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape inside a dialog closes the dialog, not the window behind it.
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const target = e.target as Element | null;
      if (target?.closest?.("[role=dialog],[role=alertdialog]")) return;
      setMaximized(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [maximized]);

  // One edit session, read by the sticky bar and the panel alike.
  const editor = useConfigEditor({
    vehicle: v,
    schema,
    token,
    readOnlyReason,
    onSaved,
  });

  const now = Date.now();
  // Freshness chrome is schema-gated (see freshnessEnabled); with it off, the
  // title carries no stale count because nothing below would explain one.
  const attention = useMemo(
    () =>
      freshnessEnabled(schema)
        ? attentionItems(schema?.fields ?? [], v.attrs, now)
        : [],
    [schema, v.attrs, now],
  );
  const ladder = useMemo(
    () => capabilityLadder(schema, v.attrs),
    [schema, v.attrs],
  );

  const sortedTickets = useMemo(() => {
    const mul = sortDir === "asc" ? 1 : -1;
    return [...v.tickets].sort((a, b) => {
      if (sortField === "severity") {
        const d = severityRank(a.op) - severityRank(b.op);
        if (d !== 0) return d * mul;
        return -cmpDate(a.updated, b.updated); // tiebreak: newest first
      }
      return cmpDate(a.updated, b.updated) * mul;
    });
  }, [v.tickets, sortField, sortDir]);

  // Sibling tractors for quick hopping — strictly the current filter's set, so
  // the rail matches the grid (SW-6136). The open tractor can fall outside the
  // filter (you're still viewing it in the main panel); it just isn't listed in
  // the rail, rather than the whole fleet reappearing. The query narrows it
  // further, against exactly what the row shows (shared with the fleet table).
  const siblings = useMemo(
    () =>
      vehicles
        .filter((x) => showRigs || !isTestRig(x))
        .filter((x) => filter === "all" || x.customer === filter)
        .filter((x) => matchesQuery(x, schema, nameMode, railQuery))
        .slice()
        .sort(vehicleComparator(railSort, nameMode)),
    [vehicles, filter, showRigs, railSort, nameMode, railQuery, schema],
  );

  /**
   * Leaving this tractor with edits in flight. The draft itself survives —
   * `useConfigEditor` keeps it in sessionStorage and restores it when this
   * tractor is opened again — but an operator who leaves without knowing that
   * has, as far as they can tell, lost their work, or worse, believes it was
   * saved. So every exit this page controls asks first, and says what happens
   * to the draft either way. Exits it does not control (the browser's Back,
   * the navbar) are covered by the restore alone.
   */
  const leave = (go: () => void) => {
    if (editor.pendingCount > 0) setPendingLeave(() => go);
    else go();
  };

  const leaveKeeping = () => {
    const go = pendingLeave;
    setPendingLeave(null);
    go?.();
  };

  const leaveDiscarding = () => {
    const go = pendingLeave;
    setPendingLeave(null);
    editor.cancel();
    go?.();
  };

  const toggleSort = (field: TicketSortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const arrow = (field: TicketSortField) =>
    field === sortField ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  /**
   * The severity slot says severity, or says there is none. It used to fall
   * back to the Jira status category ("TO DO", "IN PROGRESS") for an unrated
   * ticket, which put two vocabularies in one position; the status now sits on
   * the meta line where it belongs.
   */
  const opBadge = (k: Ticket) => {
    if (k.op) return { text: k.op.toLowerCase(), ...OP_CONFIG[k.op] };
    return {
      text: "unrated",
      bg: tokens.offWhite,
      color: tokens.slate,
      border: tokens.line,
    };
  };

  // Staleness only. A missing confirmation stamp still shows on its own field,
  // where it means something and can be acted on; as a headline count beside
  // the tractor's name it was a number nobody could do anything with.
  const staleCount = attention.filter((a) => a.kind === "stale").length;

  const name = primaryName(v, nameMode);
  const pendingWord = `${editor.pendingCount} change${editor.pendingCount === 1 ? "" : "s"}`;

  const configToggle = (
    <WindowToggle
      label="tractor config"
      expanded={maximized === "config"}
      onToggle={() => toggleWindow("config")}
    />
  );

  const mapCard = (
    <TractorMiniMap
      vehicle={v}
      expanded={maximized === "map"}
      actions={
        <WindowToggle
          label="location map"
          expanded={maximized === "map"}
          onToggle={() => toggleWindow("map")}
        />
      }
    />
  );

  const ticketsCard = (
    <TicketsCard>
      <TicketsPanel>
        <TicketsHeader>
          <Eyebrow as="h2">Open Jira tickets</Eyebrow>
          <HeaderRight>
            <TicketCount role="status">
              {detailLoading ? "loading…" : `${v.openTicketCount} open`}
            </TicketCount>
            <WindowToggle
              label="open Jira tickets"
              expanded={maximized === "tickets"}
              onToggle={() => toggleWindow("tickets")}
            />
          </HeaderRight>
        </TicketsHeader>
        {v.tickets.length > 0 ? (
          <>
            <SortBar>
              <SortLabel>Sort</SortLabel>
              <Chip
                tone="blue"
                active={sortField === "severity"}
                onClick={() => toggleSort("severity")}
              >
                Severity{arrow("severity")}
              </Chip>
              <Chip
                tone="blue"
                active={sortField === "date"}
                onClick={() => toggleSort("date")}
              >
                Date{arrow("date")}
              </Chip>
            </SortBar>
            <TicketScroll>
              {sortedTickets.map((k) => {
                const b = opBadge(k);
                const status = k.statusCategory ?? k.status;
                const age = openFor(k.created, now);
                // Older tickets predate the poller storing `created`; the
                // update's age is the honest fallback, labelled as such.
                const updatedAgo = age
                  ? null
                  : describeAge(daysSince(k.updated, now));
                return (
                  <TicketRow key={k.key}>
                    <OpBadge
                      style={{
                        background: b.bg,
                        color: b.color,
                        borderColor: b.border,
                      }}
                    >
                      {b.text}
                    </OpBadge>
                    <TicketBody>
                      <TicketSummary>{k.summary}</TicketSummary>
                      {/* The key sits on the meta line rather than in a
                          column of its own: in a side column, a right-aligned
                          key costs the summary the 60px that decides whether
                          you can tell two lidar faults apart. */}
                      <TicketMeta>
                        <TicketLink
                          href={jiraUrl(k.key)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {k.key}&nbsp;&#8599;
                          <SrOnly> (opens in Jira, new tab)</SrOnly>
                        </TicketLink>
                        <span>
                          {status && `${status} · `}
                          {k.assignee}
                        </span>
                        {/* How long the issue has stood, beside the date that
                            only says when it last moved. */}
                        {age ? (
                          <>
                            <Age title={`Raised ${shortDate(k.created)}`}>
                              {age}
                            </Age>
                            <span>updated {shortDate(k.updated)}</span>
                          </>
                        ) : (
                          <span>
                            updated {shortDate(k.updated)}
                            {updatedAgo && <Age as="span"> · {updatedAgo}</Age>}
                          </span>
                        )}
                      </TicketMeta>
                    </TicketBody>
                  </TicketRow>
                );
              })}
            </TicketScroll>
          </>
        ) : (
          /*
           * "No open tickets" is a claim, and an empty `tickets` array does not
           * support it: only the single-vehicle GET carries the list, so an
           * empty one means "none" *or* "not fetched". The count comes from
           * the fleet row and is always present — when it disagrees with the
           * list, the list is the thing that's missing, and saying so is the
           * honest answer.
           */
          <NoTickets $good={v.openTicketCount === 0}>
            {detailLoading
              ? "Loading tickets…"
              : v.openTicketCount > 0
                ? `${v.openTicketCount} open — the ticket list didn’t load. Reopen this tractor to try again.`
                : "✓  No open tickets."}
          </NoTickets>
        )}
      </TicketsPanel>
    </TicketsCard>
  );

  return (
    <Root>
      <TopBar>
        <BackButton type="button" onClick={() => leave(onBack)}>
          &#8249; All tractors
        </BackButton>
        <NameToggle value={nameMode} onChange={onNameMode} />
      </TopBar>

      <Layout>
        <SideRail aria-label="Tractors">
          <RailHeader>
            <Eyebrow as="h2">Tractors</Eyebrow>
            <RailCount
              $narrowed={Boolean(railQuery.trim()) || filter !== "all"}
            >
              {railQuery.trim() || filter !== "all"
                ? `${siblings.length} of ${vehicles.length}`
                : siblings.length}
            </RailCount>
            {/* The customer filter, moved in from its own bar above the page:
                the same shared state as the grid's chips (SW-6136), one click
                away instead of a permanent row. While a filter is applied the
                toggle turns solid and carries the customer's name, so a
                narrowed rail says why even with the drawer closed -- the
                earlier faint tint read as nothing having happened (SW-7514).
                Open with no filter, it only looks pressed. */}
            <FilterToggle
              type="button"
              $active={appliedFilter !== null}
              $open={filterOpen}
              aria-expanded={filterOpen}
              aria-label={
                appliedFilter
                  ? `Filter by customer: ${appliedFilter} applied`
                  : "Filter by customer"
              }
              title={
                appliedFilter
                  ? `Showing ${appliedFilter} only. Click to change.`
                  : "Filter by customer"
              }
              onClick={() => setFilterOpen((open) => !open)}
            >
              <FunnelIcon filled={appliedFilter !== null} />
              {appliedFilter && <FilterLabel>{appliedFilter}</FilterLabel>}
            </FilterToggle>
          </RailHeader>
          <RailControls>
            <Input
              value={railQuery}
              placeholder="Search tractors"
              aria-label="Search tractors"
              onChange={(e) => setRailQuery(e.target.value)}
            />
            <SegmentedControl
              options={RAIL_SORTS}
              value={railSort}
              onChange={setRailSort}
              ariaLabel="Sort tractor list"
            />
            {filterOpen && (
              <RailFilter role="group" aria-label="Customer">
                <FilterChips
                  vehicles={vehicles}
                  filter={filter}
                  onFilter={onFilter}
                />
              </RailFilter>
            )}
          </RailControls>
          <RailList>
            {siblings.map((s) => {
              const active = s.vehicleName === v.vehicleName;
              return (
                <RailItem
                  key={s.vehicleName}
                  type="button"
                  $active={active}
                  aria-current={active ? "page" : undefined}
                  onClick={() => !active && leave(() => onOpen(s.vehicleName))}
                >
                  <RailName>{primaryName(s, nameMode)}</RailName>
                  <RailBadge
                    $active={active}
                    aria-label={`${s.openTicketCount} open tickets`}
                  >
                    {s.openTicketCount}
                  </RailBadge>
                </RailItem>
              );
            })}
            {siblings.length === 0 && (
              <RailEmpty>No tractor matches “{railQuery.trim()}”.</RailEmpty>
            )}
          </RailList>
        </SideRail>

        <Main>
          {/* The capability cell is a Radix Tooltip, and Radix throws without a
              provider. The table and the config panel each mount their own;
              neither encloses this row, so it carries one too. 250ms is the
              fleet table's, not the config panel's 200ms: this is the table's
              cell, and the same component should not open on two timings. */}
          <TooltipProvider delayDuration={250}>
            <TitleRow>
              <Title>{name}</Title>
              <StateBadge state={v.operationalState} schema={schema} />
              <ReadinessBadge
                readiness={v.productionReadiness}
                schema={schema}
              />
              {/*
                The two things the fleet row led with, in the same shapes, so
                arriving here confirms what you clicked instead of asking you to
                find it again. The capability cell is literally the table's —
                same pips, same rung, same ragged marker. Omitted, rather than
                drawn as its "nothing recorded" dash, when there is no ladder to
                show: an orphan dash beside the name explained nothing.
              */}
              {!ladder.unknown && <CapabilityCell ladder={ladder} />}
              <TitleMeta title={secondaryName(v, nameMode)}>
                {secondaryName(v, nameMode)}
                {v.tractorId && ` · ${v.tractorId}`}
                {!v.hasAsset && " · no Jira asset"}
              </TitleMeta>
              {/* What this tractor is asking for, before the record that holds
                  it. The groups these live in are the ones already open below. */}
              {staleCount > 0 && <Badge tone="warn">{staleCount} stale</Badge>}
              {v.openBlockerCount > 0 && (
                <Badge tone="bad">
                  {v.openBlockerCount} blocker
                  {v.openBlockerCount === 1 ? "" : "s"}
                </Badge>
              )}
            </TitleRow>
          </TooltipProvider>

          {/* The edit bar belongs to the record; when another window has the
              column it would be a bar for something not on screen. */}
          {(maximized === null || maximized === "config") && (
            <ConfigActionBar editor={editor} vehicleLabel={name} />
          )}

          {/* The record first, across the full width — it is what the page is
              for, and the one thing here that gains from every pixel.
              Maximised, it becomes the column's scroll box: the panel's own
              sticky footer pins to its bottom, its header to its top. */}
          {(maximized === null || maximized === "config") && (
            <RecordCard $maximized={maximized === "config"}>
              <ConfigPanel
                vehicle={v}
                schema={schema}
                editor={editor}
                now={now}
                actions={configToggle}
                stickyHeader={maximized === "config"}
              />
              <ChangeHistoryTrigger vehicle={v} schema={schema} now={now} />
            </RecordCard>
          )}

          {/* Context beneath it, filling whatever height the record leaves —
              or one of the two, alone, when it has been maximised. */}
          {maximized === null ? (
            <>
              <ReadinessWeekCard token={token} vehicle={v} />
              <ContextGrid>
                {mapCard}
                {ticketsCard}
              </ContextGrid>
            </>
          ) : maximized === "config" ? null : (
            <Maximized>{maximized === "map" ? mapCard : ticketsCard}</Maximized>
          )}
        </Main>
      </Layout>

      {/* One prompt for both "discard" asks: leaving with a draft, and the bar's
          own Discard button. Escape and outside-click map to the safe choice. */}
      <AlertDialog
        open={pendingLeave !== null || editor.cancelPrompt}
        onOpenChange={(next) => {
          if (next) return;
          setPendingLeave(null);
          editor.dismissCancel();
        }}
      >
        <AlertDialogContent>
          {pendingLeave !== null ? (
            <>
              <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingWord} to {name}{" "}
                {editor.pendingCount === 1
                  ? "hasn’t been saved. It’ll be kept"
                  : "haven’t been saved. They’ll be kept"}{" "}
                for when you come back to this tractor, unless you discard{" "}
                {editor.pendingCount === 1 ? "it" : "them"}.
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel
                  className={cn(
                    buttonVariants({ variant: "ghost", size: "sm" }),
                  )}
                >
                  Stay
                </AlertDialogCancel>
                <AlertDialogAction
                  className={cn(
                    buttonVariants({ variant: "ghost", size: "sm" }),
                  )}
                  onClick={leaveKeeping}
                >
                  Leave, keep changes
                </AlertDialogAction>
                <AlertDialogAction
                  className={cn(
                    buttonVariants({ variant: "destructive", size: "sm" }),
                  )}
                  onClick={leaveDiscarding}
                >
                  Discard and leave
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogTitle>Discard your changes?</AlertDialogTitle>
              <AlertDialogDescription>
                {pendingWord} to {name} will be lost.
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel
                  className={cn(
                    buttonVariants({ variant: "ghost", size: "sm" }),
                  )}
                >
                  Keep editing
                </AlertDialogCancel>
                <AlertDialogAction
                  className={cn(
                    buttonVariants({ variant: "destructive", size: "sm" }),
                  )}
                  onClick={editor.cancel}
                >
                  Discard {pendingWord}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Root>
  );
};

/**
 * The page's own frame.
 *
 * On anything wider than a phone it claims the height `Page` has left — the
 * pattern the fleet grid uses — and stops there: the rail and the main column
 * scroll inside themselves, so the whole view is the viewport and no card ever
 * disappears below the fold. Narrow screens stack and flow instead; a column
 * that scrolls inside a page that also scrolls is the one thing worse than
 * scrolling.
 */
const Root = styled.div`
  width: 100%;
  box-sizing: border-box;
  flex: none;
  padding: 24px 40px 48px;

  @media (min-width: ${FILL_MIN_WIDTH}px) {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* Page reserves a scrollbar gutter on the right that it no longer uses
       (nothing here scrolls the page); count it toward the margin so the
       frame reads centred instead of heavier on the right. */
    padding: 24px 28px 24px 40px;
  }

  @media (max-width: 760px) {
    padding: 20px 16px 48px;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${tokens.slate};
  cursor: pointer;

  &:hover {
    color: ${tokens.blue};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 3px;
    border-radius: 3px;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (min-width: ${FILL_MIN_WIDTH}px) {
    /* Both columns take the frame's full height and scroll inside. */
    flex: 1;
    min-height: 0;
    align-items: stretch;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Compact navigation list of the other tractors so ops can hop between many
// without going back to the grid each time.
const SideRail = styled(Card).attrs({ as: "nav" })`
  padding: 14px 12px;
  display: flex;
  flex-direction: column;

  @media (min-width: ${FILL_MIN_WIDTH}px) {
    /* Stretched to the frame by the grid; the list scrolls within. */
    min-height: 0;
  }

  @media (max-width: 900px) {
    /* Stacked above the detail on mobile — cap it so a long fleet scrolls
       within the rail instead of pushing the tractor's own data off-screen. */
    max-height: 320px;
  }
`;

const RailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 8px 8px;

  h2 {
    margin: 0;
    flex: 1;
    min-width: 0;
    /* The lit filter toggle grows to carry a name; the eyebrow gives way
       rather than wrapping a single word. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Blue while something is narrowing the list, in step with the toggle beside
   it, so "3 of 6" and the reason for it read as one statement. */
const RailCount = styled.span<{ $narrowed: boolean }>`
  font-size: 12px;
  font-weight: ${(p) => (p.$narrowed ? 600 : 400)};
  color: ${(p) => (p.$narrowed ? tokens.blue : tokens.slate)};
  white-space: nowrap;
`;

/* Three states, each meant to be told apart at a glance:
     quiet    -- the whole fleet is listed and the drawer is closed
     open     -- the drawer is showing its chips but nothing is applied
     applied  -- a customer filter is narrowing the list: solid blue, the
                 funnel filled, and the customer's name beside it
   The old applied state was a 26px outline with an 8% tint, which is what
   "nothing lights up when I turn the filter on" looked like (SW-7514). */
const FilterToggle = styled.button<{ $active: boolean; $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 26px;
  height: 26px;
  flex: none;
  padding: ${(p) => (p.$active ? "0 10px 0 8px" : "0")};
  border-radius: ${(p) => (p.$active ? "999px" : "6px")};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  border: 1px solid
    ${(p) => (p.$active ? tokens.blue : p.$open ? tokens.slate : tokens.line)};
  background: ${(p) =>
    p.$active ? tokens.blue : p.$open ? tokens.offWhite : tokens.white};
  color: ${(p) =>
    p.$active ? tokens.white : p.$open ? tokens.ink : tokens.slate};
  cursor: pointer;
  transition:
    background-color 0.12s,
    color 0.12s,
    border-color 0.12s;

  &:hover {
    color: ${(p) => (p.$active ? tokens.white : tokens.ink)};
    border-color: ${(p) => (p.$active ? tokens.navy : tokens.slate)};
    background: ${(p) =>
      p.$active ? tokens.navy : p.$open ? tokens.offWhite : tokens.white};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

/* The applied customer, inside the lit toggle. Capped so a long tenant name
   cannot push the count and the eyebrow out of the rail. */
const FilterLabel = styled.span`
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FunnelIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M2 3h12L9.5 8.5V13l-3 1.5V8.5L2 3z"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

/* The chips wrap in a 216px column, and are set a size down from the grid's so
   three of them read as a control group rather than a stack of pills. */
const RailFilter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  button {
    padding: 5px 11px;
    font-size: 12.5px;
  }
`;

const RailControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px 10px;
  border-bottom: 1px solid ${tokens.line};
  margin-bottom: 6px;
`;

const RailList = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Room for the focus ring on the first and last items. */
  padding: 2px;
`;

const RailEmpty = styled.div`
  padding: 10px;
  font-size: 12.5px;
  color: ${tokens.slate};
`;

const RailItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: ${(p) => (p.$active ? "default" : "pointer")};
  font-family: inherit;
  background: ${(p) => (p.$active ? tokens.blue : "transparent")};
  color: ${(p) => (p.$active ? tokens.white : tokens.ink)};
  transition:
    background 0.12s,
    color 0.12s;

  &:hover {
    background: ${(p) => (p.$active ? tokens.blue : tokens.offWhite)};
  }
  /* A blue ring on the blue current item is invisible; the white inner ring is
     what makes focus readable there. */
  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${tokens.white},
      0 0 0 4px ${tokens.blue};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const RailName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 600;
`;

/* Solid white on the current item rather than a translucent wash: the wash
   put white digits on #388AFF at 3.4:1. */
const RailBadge = styled.span<{ $active: boolean }>`
  flex: none;
  font-family: ${tokens.fontMono};
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;
  padding: 1px 8px;
  background: ${(p) => (p.$active ? tokens.white : tokens.offWhite)};
  color: ${(p) => (p.$active ? tokens.blue : tokens.slate)};
  border: 1px solid ${(p) => (p.$active ? tokens.white : tokens.line)};
`;

/**
 * The main column. Wide, it is a scroll box of its own: the title stays put
 * while the record scrolls under the sticky edit bar, and the context row at
 * the bottom grows into whatever height the record leaves — see ContextGrid.
 */
const Main = styled.div`
  min-width: 0;

  @media (min-width: ${FILL_MIN_WIDTH}px) {
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    /* Reserved whether or not it is needed, so maximising a card — which
       changes whether the column overflows — does not shift every card's
       width by a scrollbar. */
    scrollbar-gutter: stable;
    /* No padding on purpose: sticky children (the edit bar at the top, the
       record's footer at the bottom) pin to a scroll container's *content*
       edge, so any padding here leaves a sliver where the content scrolling
       underneath shows past the pinned bar. */
    padding: 0;
  }
`;

/** One card, alone, filling the column. */
const Maximized = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${FILL_MIN_WIDTH}px) {
    flex: 1;
    min-height: 0;

    & > * {
      flex: 1;
      min-height: 0;
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 34px;
  font-weight: 300;
  letter-spacing: -0.02em;
`;

const TitleMeta = styled.span`
  font-family: ${tokens.fontMono};
  font-size: 13px;
  color: ${tokens.slate};
`;

/**
 * The record, full width.
 *
 * It holds the config panel and the change-history line beneath it inside one
 * card, because they are the same subject: what this tractor is configured as,
 * and who last changed that. The history tree itself is behind the line, in an
 * overlay.
 */
const RecordCard = styled(Card)<{ $maximized: boolean }>`
  min-width: 0;
  flex: none;
  margin-bottom: 16px;

  ${(p) =>
    p.$maximized
      ? `@media (min-width: ${FILL_MIN_WIDTH}px) {
           flex: 1;
           min-height: 0;
           overflow-y: auto;
           margin-bottom: 0;
         }`
      : ""}
`;

/**
 * Context under the record: location, tickets, and whatever comes next.
 *
 * One card per row until there is genuinely space for two. Below that the map
 * takes the full width — a 340px-wide map beside a 340px-wide ticket list is
 * two cramped things instead of one useful one, and the map is the card that
 * suffers most from being narrow.
 *
 * Explicit tracks rather than `auto-fit` + `grid-column: span 2`, which is what
 * this was and was subtly broken. Track *count* comes from the container while
 * a media query measures the **viewport**, and the two disagree by ~640px here
 * (the nav, the page padding, the sibling rail). Around 1100–1350px wide the
 * query said "span 2 columns" while the container had only afforded one, so the
 * grid invented an implicit second track and the row overflowed — which is
 * exactly the squeezed map that prompted this. Two named tracks cannot do that.
 *
 * Adding a metrics card later: give it its own row, or widen this to three
 * tracks at a breakpoint where the container can actually hold them.
 */
const ContextGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  /* Stretch, so cards sharing a row share a height and read as one band. A
     card with nothing to fill the space with opts out with align-self: start —
     see TractorMiniMap. */
  align-items: stretch;

  /* Grid tracks have a min-content floor; without this the tickets card's
     long summaries blow the column past the viewport instead of wrapping. */
  & > * {
    min-width: 0;
  }

  @media (min-width: ${FILL_MIN_WIDTH}px) {
    /* Take the height the record leaves, never less than a useful ticket
       list; past that the column scrolls. Stacked, the map row is sized by
       the map and the tickets take the rest. */
    flex: 1 1 auto;
    min-height: 340px;
    grid-template-rows: auto minmax(0, 1fr);

    & > * {
      min-height: 0;
    }
  }

  /* Tickets take the larger share: a Jira summary is a sentence, and a map
     reads fine at half the width of one. */
  @media (min-width: 1500px) {
    grid-template-columns: minmax(360px, 1fr) minmax(0, 2fr);
    grid-template-rows: minmax(0, 1fr);
  }
`;

const TicketsCard = styled.div`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* A column, so the list can take the card's leftover height and scroll. */
const TicketsPanel = styled(Card)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TicketsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 20px 4px;
  flex: none;

  h2 {
    margin: 0;
  }
`;

const HeaderRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const TicketCount = styled.span`
  font-size: 12.5px;
  color: ${tokens.slate};
  white-space: nowrap;
`;

const SortBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 20px 12px;
  flex: none;
`;

const SortLabel = styled(Eyebrow)`
  margin-right: 2px;
`;

// Open-ticket lists can be long (100+ noisy auto-tickets). Wide, the list takes
// whatever height its card was given and scrolls inside it; in the flowing
// narrow layout it is capped instead, so one tractor can't blow out the page.
const TicketScroll = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media (max-width: 900px) {
    max-height: 460px;
  }
`;

const TicketRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid ${tokens.line};
`;

const OpBadge = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 5px;
  padding: 3px 8px;
  flex: none;
  border: 1px solid;
  white-space: nowrap;
`;

const TicketBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const TicketSummary = styled.div`
  font-size: 14px;
  font-weight: 500;
  /* Two lines rather than one: the column is narrower than it was, and a Jira
     summary truncated at 40 characters usually loses the part that identifies
     which fault it is. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TicketMeta = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${tokens.slate};
  margin-top: 3px;

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** The age, set apart from the dates around it so it can be read down the list. */
const Age = styled.span`
  flex: none;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${tokens.ink};
`;

const TicketLink = styled.a`
  /* Contains the visually-hidden span below. Absolutely positioned with no
     positioned ancestor it escaped the ticket list's scroll box, and a hundred
     of them stretched the document to eight thousand pixels. */
  position: relative;
  flex: none;
  font-family: ${tokens.fontMono};
  font-size: 12px;
  font-weight: 600;

  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const NoTickets = styled.div<{ $good: boolean }>`
  flex: none;
  padding: 18px 20px;
  border-top: 1px solid ${tokens.line};
  font-size: 14px;
  color: ${(p) => (p.$good ? tokens.readyText : tokens.slate)};
  font-weight: 500;
`;

export default TractorDetailView;
